import { Dispatch } from 'redux';
import * as Snackbar from '@synergy/shared/snackbar/data-access';
import { PoolNotification } from '@synergy/shared/snackbar/data-access';

/** Anything the document holds a partner in: an account, as the mask shows it. */
export interface LockedPartner {
    name?: string;
    isLocked?: boolean;
}

/** A person the document names, as the list carries them. */
export interface LockedPerson {
    fullName?: string;
    isLocked?: boolean;
}

/**
 * A contact person read as a partner, so one notice covers both.
 *
 * A locked contact is left out of a copy exactly as a locked partner is: the
 * order would otherwise go to somebody who has left the company (GF
 * 13.10.2026, #2230 B-1).
 */
export const lockedPerson = (person?: LockedPerson): LockedPartner | undefined =>
    person ? { name: person.fullName, isLocked: person.isLocked } : undefined;

/**
 * Says that a locked partner was left out of a copy.
 *
 * A document carries its partner as a copy of the master record at the time it
 * was written, so a partner locked since then still stands in it. The copy
 * leaves that partner out — it may no longer be ordered from — and until now
 * did so without a word, which read as a lost field. "Das System muss das beim
 * Erstellen melden und den Partner zur Neuauswahl leeren" (GF 12.10.2026,
 * #2230).
 *
 * Takes the partners of the document that was copied; every one of them that
 * is locked today is named once.
 */
export const noticeLockedPartners = (dispatch: Dispatch, partners: (LockedPartner | undefined)[]): void => {
    const named = new Set<string>();

    partners.forEach(partner => {
        if (partner?.isLocked && partner.name && !named.has(partner.name)) {
            named.add(partner.name);
            dispatch(
                Snackbar.enqueue(
                    PoolNotification.warning('app.snackbar.lockedPartnerDropped', { name: partner.name })
                )
            );
        }
    });
};

/** The partners an inquiry carries: one per buying member, one per offer. */
export const inquiryPartners = (inquiry: {
    accountPurchases?: {
        customerAccount?: LockedPartner;
        contact?: LockedPerson;
        offers?: { supplierAccount?: LockedPartner; supplierContact?: LockedPerson }[];
    }[];
}): (LockedPartner | undefined)[] =>
    (inquiry.accountPurchases ?? []).flatMap(accountPurchase => [
        accountPurchase.customerAccount,
        lockedPerson(accountPurchase.contact),
        ...(accountPurchase.offers ?? []).flatMap(offer => [
            offer.supplierAccount,
            lockedPerson(offer.supplierContact),
        ]),
    ]);
